import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { useObrasListaViewModel } from "../../viewModels/obras_lista_view_model";
import { Header } from "./components/header";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendIcon } from "../../../../../common/components/legend_icon";
import { LegendItemComponent } from "../../../../../common/components/legend_item_component";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateObrasColumnDefinition } from "./columns";
import { Obra } from "../../../domain/models/obra";
import { IrParaModal } from "./components/ir_para_modal";
import { CopyObraModal } from "./components/copy_obra_modal";
import { TipoCadastroObraModal } from "./components/tipo_cadastro_obra_modal";
import { SendMailModal } from "../../../../../common/components/send_email_modal";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";

import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * ObrasLista
 */

export const ObrasLista = () => {
  const viewModel = useObrasListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateObrasColumnDefinition({
      onActionExportar: viewModel.handleReportPlanoQualidadeSubmit,
      onActionEditar: viewModel.handleCadastroObraOpenState,
      onActionDeletar: viewModel.handleDeleteModalOpenState,
      onActionCopiar: viewModel.handleCopyModalOpenState,
      onActionEnviarEmail: viewModel.handleOpenEmailOpenState,
      onActionIrPara: viewModel.handleIrParaModalOpenState,
    });
  }, [
    viewModel.handleReportPlanoQualidadeSubmit,
    viewModel.handleCadastroObraOpenState,
    viewModel.handleIrParaModalOpenState,
    viewModel.handleCopyModalOpenState,
    viewModel.handleOpenEmailOpenState,
    viewModel.handleDeleteModalOpenState,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="w-[130px] ">
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                onMouseDown={e => {
                  if (e.button === 1) {
                    viewModel.handleTipoCadastroModalOpenState(true, true);
                  } else {
                    viewModel.handleTipoCadastroModalOpenState(true, e.ctrlKey);
                  }
                }}>
                Nova Obra
              </PrimaryButton>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Obras
          </Link>
          <Typography className="text-primary-green">
            Lista de Obras
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Lista de Obras</h1>
            <RefreshButton onClick={viewModel.handleReload} />
          </div>

          <div className="w-full justify-end flex gap-2"></div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  tipoPesquisa={viewModel.filters.tipoPesquisa}
                  valueField={viewModel.filters.valueField}
                  statusDocumentos={viewModel.filters.statusDocumentos}
                  resistenciaCompressao={viewModel.filters.resistenciaCompressao}
                  statusEquipamentos={viewModel.filters.statusEquipamentos}
                  statusObras={viewModel.filters.statusObras}
                  centroCustosDropdown={viewModel.dropdown.centroCustosDropdown}
                  empreendimentosDropdown={viewModel.dropdown.empreendimentosDropdown}
                  onTipoPesquisaChange={viewModel.handleTipoPesquisaChange}
                  onValueFieldChange={viewModel.handleValueFieldChange}
                  onStatusDocumentosChange={viewModel.handleStatusDocumentosChange}
                  onResistenciaCompressaoChange={viewModel.handleResistenciaCompressaoChange}
                  onStatusEquipamentosChange={viewModel.handleStatusEquipamentosCompressaoChange}
                  onStatusObrasChange={viewModel.handleStatusObrasChange}
                  onInformacaoClearFilters={viewModel.handleInformacaoClearFilters}
                />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-stretch">
                    <div className="flex flex-col flex-wrap">
                      <LegendComponent label="Status">
                        <LegendItemComponent
                          icon={<LegendIcon color='green' title='A' />}
                          label='Em andamento'
                        />

                        <LegendItemComponent
                          icon={<LegendIcon color='yellow' title='P' />}
                          label='Paralisada'
                        />

                        <LegendItemComponent
                          icon={<LegendIcon color='red' title='F' />}
                          label='Finalizada'
                        />
                      </LegendComponent>
                    </div>

                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <StyledDataGrid
                    tableName="obras_lista"
                    disableColumnMenu={false}
                    getRowId={(row: Obra) => row.idObra}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.obras}
                    rowCount={viewModel.obras.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                </Stack>
              </>
            )
        }

        {/**
          * Modals
          */}

        <IrParaModal
          open={viewModel.openIrParaModal}
          selectedObra={viewModel.selectedObra ?? null}
          onClose={() => viewModel.handleIrParaModalOpenState(null, false)}
          onExecuteAction={viewModel.handleIrParaExecuteAction}
        />

        <TipoCadastroObraModal
          open={viewModel.openTipoCadastroModal}
          onClose={() => viewModel.handleTipoCadastroModalOpenState(false)}
          onSelect={viewModel.handleTipoCadastroSelect}
        />

        <CopyObraModal
          open={viewModel.openCopyModal}
          saving={viewModel.saving}
          centroCustosDropdown={viewModel.dropdown.centroCustosDropdown}
          onClose={viewModel.handleCopyModalOpenState}
          onSubmit={viewModel.handleConfirmCopy}
        />

        <DeleteDialog
          open={viewModel.openDeleteModal}
          saving={viewModel.saving}
          onClose={() => viewModel.handleDeleteModalOpenState(null, false)}
          title={`Você realmente deseja excluir este item?`}
          onDelete={viewModel.handleConfirmarDeletarObra}
        />

        {/**
          * Email modal
          */}

        <SendMailModal
          open={viewModel.openEmailModal}
          loading={viewModel.loading}
          sendTo={viewModel.emailSendTo}
          subject={viewModel.emailSubject}
          attachment={viewModel.emailAttachment}
          body={viewModel.emailBody}
          onClose={() => viewModel.handleOpenEmailOpenState(null, false)}
          onSubmit={viewModel.handleEmailSubmit}
        />

        <ReportViewerModal
          reportViewerState={viewModel.reportViewerState}
        />

      </div>
    </>
  );
}